"use client";

import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { useDispatch } from "react-redux";
import ProductTableItem from "./ProductTableItem";
import ProductDetailsModal from "./ProductDetailsModal";
import Api from "../../api/Api";

const Products = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  document.title = "Products Management | Tafaseel Admin";

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await Api.get("/admin/products");
      if (response.data.success) {
        setProducts(response.data.data);
        toast.success("Products loaded successfully");
      } else {
        toast.error(response.data.message || "Failed to load products");
      }
    } catch (error) {
      console.error("Load products error:", error);
      toast.error(error.response?.data?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  function tog_center() {
    setmodal_center(!modal_center);
    setSelectedProduct(null);
  }

  const handleShow = (product) => {
    setSelectedProduct(product);
    setmodal_center(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        const response = await Api.delete(`/admin/products/${productId}`);
        if (response.data.success) {
          toast.success("Product deleted successfully");
          loadProducts(); // Reload the products list
        } else {
          toast.error(response.data.message || "Failed to delete product");
        }
      } catch (error) {
        console.error("Delete product error:", error);
        toast.error(
          error.response?.data?.message || "Failed to delete product"
        );
      }
    }
  };

  const handleStatusUpdate = (productId, newStatus) => {
    // Update the product in the local state
    setProducts((prevProducts) =>
      prevProducts.map((product) =>
        product.id === productId ? { ...product, status: newStatus } : product
      )
    );

    // Update filtered data if it exists
    if (filteredData.length > 0) {
      setFilteredData((prevFiltered) =>
        prevFiltered.map((product) =>
          product.id === productId ? { ...product, status: newStatus } : product
        )
      );
    }
  };

  const handleSearch = (text) => {
    const newQuery = text.target.value.toLowerCase();
    if (newQuery === "") {
      setFilteredData([]);
    } else {
      const filtered = products.filter(
        (product) =>
          product.title.toLowerCase().includes(newQuery) ||
          product.description?.toLowerCase().includes(newQuery) ||
          product.sku?.toLowerCase().includes(newQuery) ||
          product.user?.name.toLowerCase().includes(newQuery) ||
          product.category?.name.toLowerCase().includes(newQuery) ||
          product.id.toString().includes(newQuery)
      );
      setFilteredData(filtered);
    }
  };

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const displayProducts = filteredData.length > 0 ? filteredData : products;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(displayProducts.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="Admin" breadcrumbItem="Products Management" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="productsList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={loadProducts}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search products..."
                              onChange={(e) => handleSearch(e)}
                            />
                            <i className="ri-search-line search-icon"></i>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table
                        className="table align-middle table-nowrap"
                        id="productsTable"
                      >
                        <thead className="table-light">
                          <tr>
                            <th className="sort" data-sort="product_id">
                              ID
                            </th>
                            <th className="sort" data-sort="product_title">
                              Product
                            </th>
                            <th className="sort" data-sort="category">
                              Category
                            </th>
                            <th className="sort" data-sort="owner">
                              Owner
                            </th>
                            <th className="sort" data-sort="price">
                              Price
                            </th>
                            <th className="sort" data-sort="status">
                              Status
                            </th>
                            <th className="sort" data-sort="stock">
                              Stock
                            </th>
                            <th className="sort" data-sort="date">
                              Created
                            </th>
                            <th className="sort" data-sort="action">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {loading ? (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <div
                                  className="spinner-border text-primary"
                                  role="status"
                                >
                                  <span className="sr-only">Loading...</span>
                                </div>
                                <p className="mt-2 mb-0">Loading products...</p>
                              </td>
                            </tr>
                          ) : currentItems.length > 0 ? (
                            currentItems.map((product) => (
                              <ProductTableItem
                                key={product.id}
                                product={product}
                                handleShow={handleShow}
                                handleDelete={handleDelete}
                              />
                            ))
                          ) : (
                            <tr>
                              <td colSpan="9" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="ri-shopping-bag-line fs-1 mb-3 d-block"></i>
                                  <h5>No Products Found</h5>
                                  <p>No products match your search criteria.</p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}
                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {modal_center && (
            <ProductDetailsModal
              modal={modal_center}
              toggle={tog_center}
              product={selectedProduct}
              onStatusUpdate={handleStatusUpdate}
            />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Products;
